// src/components/common/CustomDatePicker.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const formatLocalDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const CustomDatePicker = ({ selectedDate, onDateChange, placeholder = "Tarih Seçin", label = "", minDate = null, showTime = false }) => {
    // Başlangıçta gelen tarihin geçerli olup olmadığını kontrol et
    const initialDate = selectedDate instanceof Date && !isNaN(selectedDate)
        ? selectedDate
        : (typeof selectedDate === 'string' && !isNaN(new Date(selectedDate)) ? new Date(selectedDate) : new Date());

    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(initialDate);
    const [timeHours, setTimeHours] = useState(initialDate.getHours().toString().padStart(2, '0'));
    const [timeMinutes, setTimeMinutes] = useState(initialDate.getMinutes().toString().padStart(2, '0'));
    const datePickerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const daysOfWeek = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
    const monthNames = [
        "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
        "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
    ];

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let startDayOfWeek = firstDayOfMonth.getDay();
    if (startDayOfWeek === 0) startDayOfWeek = 7;

    const calendarDays = [];
    for (let i = 1; i < startDayOfWeek; i++) {
        calendarDays.push(<div key={`empty-start-${i}`} className="p-1 border border-transparent"></div>);
    }

    const minDateObj = minDate ? new Date(minDate + 'T00:00:00') : null;

    for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const isToday = currentDate.toDateString() === new Date().toDateString();
        const isSelected = selectedDate && (new Date(selectedDate + 'T00:00:00')).toDateString() === currentDate.toDateString();
        const isDisabled = minDateObj && currentDate < minDateObj;

        calendarDays.push(
            <div
                key={day}
                onClick={(e) => {
                    if (isDisabled) return;
                    e.stopPropagation();
                    
                    if (showTime) {
                        const newDateStr = `${formatLocalDateToYYYYMMDD(currentDate)}T${timeHours}:${timeMinutes}:00`;
                        onDateChange(newDateStr);
                        // showTime ise menüyü kapatma ki saat seçebilsin
                        setViewDate(currentDate); 
                    } else {
                        onDateChange(formatLocalDateToYYYYMMDD(currentDate));
                        setIsOpen(false);
                    }
                }}
                className={`p-1 cursor-pointer rounded-md text-center text-xs
                            ${isDisabled ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}` +
                    `${isToday && !isDisabled ? ' bg-blue-200 dark:bg-blue-700 text-blue-900 dark:text-blue-100' : ''}` +
                    `${isSelected && !isDisabled ? ' bg-blue-600 text-white font-bold' : ''}` +
                    `${!isDisabled && !isToday && !isSelected ? ' text-gray-800 dark:text-gray-200' : ''} transition-colors duration-100`}
            >
                {day}
            </div>
        );
    }

    const handlePreviousMonth = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setViewDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() - 1);
            return newDate;
        });
    };

    const handleNextMonth = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setViewDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + 1);
            return newDate;
        });
    };

    const handleYearChange = (e) => {
        e.stopPropagation();
        const newYear = parseInt(e.target.value);
        setViewDate(prev => {
            const newDate = new Date(prev);
            newDate.setFullYear(newYear);
            return newDate;
        });
    };

    const handleMonthChange = (e) => {
        e.stopPropagation();
        const newMonth = parseInt(e.target.value);
        setViewDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newMonth);
            return newDate;
        });
    };

    const handleTimeChange = (type, value) => {
        let newHours = timeHours;
        let newMinutes = timeMinutes;

        if (type === 'hours') {
            newHours = value.toString().padStart(2, '0');
            setTimeHours(newHours);
        } else {
            newMinutes = value.toString().padStart(2, '0');
            setTimeMinutes(newMinutes);
        }

        // Eğer seçili bir tarih varsa (örneğin önceden tıklanmışsa veya initial ise) saat değiştiğinde direkt onDateChange tetikleyelim
        if (selectedDate) {
            const baseDate = selectedDate instanceof Date && !isNaN(selectedDate)
                ? selectedDate
                : (typeof selectedDate === 'string' && !isNaN(new Date(selectedDate)) ? new Date(selectedDate) : new Date());
            
            const newDateStr = `${formatLocalDateToYYYYMMDD(baseDate)}T${newHours}:${newMinutes}:00`;
            onDateChange(newDateStr);
        }
    };

    const formattedInputDate = selectedDate instanceof Date && !isNaN(selectedDate)
        ? selectedDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + (showTime ? ` ${selectedDate.getHours().toString().padStart(2, '0')}:${selectedDate.getMinutes().toString().padStart(2, '0')}` : '')
        : (typeof selectedDate === 'string' && !isNaN(new Date(selectedDate)) 
            ? new Date(selectedDate + (selectedDate.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + 
              (showTime ? ` ${new Date(selectedDate + (selectedDate.includes('T') ? '' : 'T00:00:00')).getHours().toString().padStart(2, '0')}:${new Date(selectedDate + (selectedDate.includes('T') ? '' : 'T00:00:00')).getMinutes().toString().padStart(2, '0')}` : '')
            : '');

    return (
        <div className="relative w-full" ref={datePickerRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {label}
                </label>
            )}
            <div className="relative">
                <input
                    type="text"
                    placeholder={placeholder}
                    value={formattedInputDate}
                    readOnly
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors cursor-pointer"
                />
                <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-300 pointer-events-none" size={18} />
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg p-3 w-48 md:w-56">
                    <div className="flex justify-between items-center mb-3 px-1">
                        <button type="button" onClick={handlePreviousMonth} className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                            <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center space-x-0.5">
                            <select
                                value={month}
                                onChange={handleMonthChange}
                                className="bg-transparent text-sm font-semibold text-gray-800 dark:text-gray-100 py-0.5 px-1 rounded-md cursor-pointer appearance-none text-center"
                            >
                                {monthNames.map((name, index) => (
                                    <option key={index} value={index}>{name}</option>
                                ))}
                            </select>
                            <select
                                value={year}
                                onChange={handleYearChange}
                                className="bg-transparent text-sm font-semibold text-gray-800 dark:text-gray-100 py-0.5 px-1 rounded-md cursor-pointer appearance-none text-center"
                            >
                                {Array.from({ length: 111 }, (_, i) => year - 100 + i).map((yr) => (
                                    <option key={yr} value={yr}>{yr}</option>
                                ))}
                            </select>
                        </div>
                        <button type="button" onClick={handleNextMonth} className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-7 gap-0.5 text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">
                        {daysOfWeek.map(day => <div key={day} className="text-center">{day}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                        {calendarDays}
                    </div>
                    {showTime && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center px-1">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Saat:</span>
                            <div className="flex items-center space-x-1">
                                <select 
                                    value={parseInt(timeHours)} 
                                    onChange={(e) => handleTimeChange('hours', e.target.value)}
                                    className="p-1 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 text-gray-900 dark:text-gray-100"
                                >
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                                    ))}
                                </select>
                                <span className="font-bold text-gray-500 dark:text-gray-400">:</span>
                                <select 
                                    value={parseInt(timeMinutes)} 
                                    onChange={(e) => handleTimeChange('minutes', e.target.value)}
                                    className="p-1 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 text-gray-900 dark:text-gray-100"
                                >
                                    {Array.from({ length: 60 }, (_, i) => (
                                        <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker;